import { memo, useMemo, useEffect, useState } from 'react';

import { DataHandler } from '../../top-scorer/utils/DataHandler';
import { getKind, getImageData } from '../../top-scorer/utils/misc';
import type { PlayerResponse } from '../../top-scorer/utils/types';
import type { RelevancyCardProps } from '../DynamicCardComponent';
import { useRelevancyOptions } from '../store';

import type { SandEComponentProps } from './SandEComponent';
import SandEComponent from './SandEComponent';

import type { EditorialContent } from '@cobalt/utilities';
import { Order } from '@shared-types/common.types';
import { competitionId, competitionUrl } from '@utilities/WindowContext';
const cardConfig: Partial<SandEComponentProps> = {
  trackingData: {
    name: 'Top scorer',
    category: 'sponsored card',
  },
  url: `/${competitionUrl}/top-scorer/`,
} as const;

const TopScorerComponent = (props: RelevancyCardProps) => {
  const [topScorerPlayer, setTopScorerPlayer] = useState<PlayerResponse>(null);
  const [topScorerPlayerEditorial, setTopScorerPlayerEditorial] = useState<EditorialContent[]>();

  const { options } = useRelevancyOptions();

  const api = useMemo(
    () =>
      DataHandler.getInstance({
        season: options.topScorerSeason,
        cup: competitionId,
        optionalFields: 'PLAYER,TEAM',
        offset: 0,
        limit: 1,
        order: Order.DESC,
        usePhase: true,
        phase: options?.phase ?? 'TOURNAMENT',
      }),
    [options.topScorerSeason, options?.phase],
  );

  useEffect(() => {
    const loadTopScorer = async () => {
      const players = api && (await api.loadPlayersInfo());
      if (players && players.length > 0) {
        setTopScorerPlayer(players[0]);
      }
    };
    loadTopScorer();
  }, [api]);

  useEffect(() => {
    const loadTopScorerEditorial = async () => {
      if (topScorerPlayer?.player?.id) {
        const playerEditorial = api && (await api.loadEditorialInfo(topScorerPlayer?.player?.id));
        setTopScorerPlayerEditorial(playerEditorial);
      }
    };
    loadTopScorerEditorial();
  }, [api, topScorerPlayer]);

  const kind = useMemo(
    () => getKind(topScorerPlayer, topScorerPlayerEditorial),
    [topScorerPlayer, topScorerPlayerEditorial],
  );

  const image = useMemo(
    () => getImageData(topScorerPlayer, topScorerPlayerEditorial),
    [topScorerPlayer, topScorerPlayerEditorial],
  );

  return (
    <SandEComponent
      image={image}
      isVideoCard={kind === 'video'}
      fetchArticle={false}
      cardType='topscorer'
      {...props}
      {...cardConfig}
    />
  );
};

export default memo(TopScorerComponent);
